import { useTranslation } from "react-i18next";
import type { ChipTone } from "../components/console/Console";
import type { InvoiceStatus, PaymentMethod, PensionStatus, PkLot, SpaceStatus, TicketStatus } from "../api/parkingApi";

// ===========================================================================
// Control de estacionamientos — piezas compartidas: dinero, duraciones, tonos
// de estado, colores del mapa de cajones y selector de estacionamiento. Sólo
// tokens semánticos (claro y oscuro).
// ===========================================================================

export const PARKING_BASE = "/nuvex-access/estacionamientos";

export function money(value: number | null | undefined, locale = "es-MX"): string {
  return new Intl.NumberFormat(locale.startsWith("en") ? "en-US" : "es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 }).format(Number(value) || 0);
}

/** `125` → `2 h 05 min`. */
export function duration(minutes: number | null | undefined): string {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  const mm = m % 60;
  if (d) return `${d} d ${h} h`;
  if (h) return `${h} h ${String(mm).padStart(2, "0")} min`;
  return `${mm} min`;
}

export function minutesBetween(fromIso?: string | null, toIso?: string | null, nowMs = Date.now()): number {
  if (!fromIso) return 0;
  const a = Date.parse(fromIso);
  const b = toIso ? Date.parse(toIso) : nowMs;
  return Math.max(0, Math.round((b - a) / 60000));
}

export const TICKET_TONE = { open: "info", paid: "success", exited: "neutral", lost: "warning", cancelled: "error" } as const satisfies Record<TicketStatus, ChipTone>;
export const PENSION_TONE = { active: "success", expiring: "warning", expired: "error", scheduled: "info", suspended: "neutral", cancelled: "neutral" } as const satisfies Record<PensionStatus, ChipTone>;
export const INVOICE_TONE = { requested: "info", ready_to_stamp: "warning", issued: "success", rejected: "error" } as const satisfies Record<InvoiceStatus, ChipTone>;
export const METHOD_ICON: Record<PaymentMethod, string> = {
  cash: "ri-money-dollar-circle-line",
  card_terminal: "ri-bank-card-line",
  stripe: "ri-secure-payment-line",
  link: "ri-links-line",
  free: "ri-gift-line",
};

/** Colores del mapa de cajones (fondo + borde + texto), con tokens del tema. */
export const SPACE_STYLE: Record<SpaceStatus, string> = {
  free: "bg-success/15 border-success/40 text-success",
  occupied: "bg-error/15 border-error/40 text-error",
  reserved: "bg-warning/15 border-warning/50 text-warning",
  pension: "bg-info/15 border-info/40 text-info",
  out_of_service: "bg-subtle border-line text-content-faint",
};
export const SPACE_DOT: Record<SpaceStatus, string> = {
  free: "bg-success",
  occupied: "bg-error",
  reserved: "bg-warning",
  pension: "bg-info",
  out_of_service: "bg-content-faint",
};
export const SPACE_TYPE_ICON: Record<string, string> = {
  standard: "",
  compact: "ri-roadster-line",
  disabled: "ri-wheelchair-line",
  ev: "ri-charging-pile-2-line",
  motorcycle: "ri-motorbike-line",
  vip: "ri-vip-crown-line",
};
export const LOT_ICON: Record<string, string> = {
  covered: "ri-home-gear-line",
  open: "ri-sun-line",
  multilevel: "ri-building-line",
  underground: "ri-arrow-down-double-line",
};

export function LotSelect({ lots, value, onChange, allowAll = true }: { lots: PkLot[]; value: string; onChange: (v: string) => void; allowAll?: boolean }) {
  const { t } = useTranslation("parking");
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={t("common.lot")}
      className="max-w-[260px] rounded-lg border border-line bg-card px-2.5 py-1.5 text-sm text-content outline-none focus:border-primary-500"
    >
      {allowAll && <option value="">{t("common.allLots")}</option>}
      {lots.map((l) => (
        <option key={l.id} value={l.id}>
          {l.name}
        </option>
      ))}
    </select>
  );
}

/** Placa estilo matrícula mexicana (sólo presentación). */
export function PlateBadge({ plate, size = "sm" }: { plate?: string | null; size?: "sm" | "lg" }) {
  if (!plate) return <span className="text-xs text-content-faint">—</span>;
  return (
    <span className={`inline-flex items-center rounded border border-line bg-surface font-mono font-semibold tracking-wider text-content ${size === "lg" ? "px-2.5 py-1 text-base" : "px-1.5 py-0.5 text-[11px]"}`}>
      {plate}
    </span>
  );
}

/** Mensaje del error del API (ya traducido por el servidor) o uno genérico. */
export function errorText(err: unknown, fallback: string): string {
  const meta = (err as { response?: { data?: { meta?: { message?: string } } } })?.response?.data?.meta;
  return meta?.message || fallback;
}

// Estilo de gráficas (tokens del tema).
export const chartTick = { fill: "rgb(var(--c-content-muted))", fontSize: 11 };
export const chartGrid = "rgb(var(--c-border))";
export const chartTooltip = {
  contentStyle: { background: "rgb(var(--c-card))", border: "1px solid rgb(var(--c-border))", borderRadius: 8, color: "rgb(var(--c-content))", fontSize: 12 },
  labelStyle: { color: "rgb(var(--c-content))" },
  cursor: { fill: "rgb(var(--c-subtle))" },
};

/** Barra de ocupación con color según el porcentaje. */
export function OccupancyBar({ pct }: { pct: number }) {
  const tone = pct >= 90 ? "bg-error" : pct >= 70 ? "bg-warning" : "bg-success";
  return (
    <span className="block h-1.5 w-full overflow-hidden rounded-full bg-subtle">
      <span className={`block h-full rounded-full ${tone}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
    </span>
  );
}
